import React from "react";
import {
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaInstagram,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { FiArrowRight, FiHeart } from "react-icons/fi";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const Footer = () => {
  const { user } = useAuth();
  const location = useLocation();
  const isHome = location.pathname === "/";

  return (
    <footer className="relative bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900 text-slate-700 dark:text-slate-300 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-blue-600/20 to-purple-600/20 transform rotate-12 scale-150"></div>
      </div>

      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Brand Section */}
            <div className="lg:col-span-1">
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 bg-gradient-to-r from-blue-600 dark:from-blue-400 to-purple-600 dark:to-purple-400 bg-clip-text text-transparent">
                  EduConsultancy
                </h2>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                  Empowering students with the right guidance and resources to
                  achieve their academic dreams worldwide.
                </p>
              </div>

              {/* Social Links */}
              <div className="flex space-x-4">
                {[
                  {
                    icon: FaFacebook,
                    href: "#",
                    color: "hover:text-blue-500 dark:hover:text-blue-400",
                  },
                  {
                    icon: FaTwitter,
                    href: "#",
                    color: "hover:text-sky-500 dark:hover:text-sky-400",
                  },
                  {
                    icon: FaLinkedin,
                    href: "#",
                    color: "hover:text-blue-600 dark:hover:text-blue-500",
                  },
                  {
                    icon: FaInstagram,
                    href: "#",
                    color: "hover:text-pink-500 dark:hover:text-pink-400",
                  },
                ].map(({ icon: Icon, href, color }, index) => (
                  <a
                    key={index}
                    href={href}
                    className={`w-10 h-10 bg-slate-200/50 dark:bg-white/10 backdrop-blur-xl rounded-full flex items-center justify-center text-slate-500 dark:text-slate-400 ${color} transition-all duration-200 hover:scale-110 hover:bg-slate-300/50 dark:hover:bg-white/20`}
                  >
                    <Icon size={18} />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-6 relative">
                Quick Links
                <div className="absolute -bottom-2 left-0 w-8 h-0.5 bg-gradient-to-r from-blue-500 dark:from-blue-400 to-purple-500 dark:to-purple-400"></div>
              </h3>
              <ul className="space-y-3">
                {[
                  {
                    label: "Home",
                    to: isHome
                      ? "#home"
                      : !user
                      ? "/"
                      : user.role === "admin"
                      ? "/admin/dashboard"
                      : "/user/dashboard",
                    isAnchor: isHome,
                  },
                  {
                    label: "About Us",
                    to: "/about",
                    isAnchor: isHome && "#about",
                  },

                  {
                    label: "Blogs",
                    to: "/blogs",
                    isAnchor: false,
                  },
                ].map(({ label, to, isAnchor }, index) => (
                  <li key={index}>
                    {isAnchor ? (
                      <a
                        href={to}
                        className="group flex items-center text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all duration-200"
                      >
                        <FiArrowRight
                          className="mr-2 transform group-hover:translate-x-1 transition-transform duration-200"
                          size={14}
                        />
                        {label}
                      </a>
                    ) : (
                      <Link
                        to={to}
                        className="group flex items-center text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all duration-200"
                      >
                        <FiArrowRight
                          className="mr-2 transform group-hover:translate-x-1 transition-transform duration-200"
                          size={14}
                        />
                        {label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-6 relative">
                Resources
                <div className="absolute -bottom-2 left-0 w-8 h-0.5 bg-gradient-to-r from-blue-500 dark:from-blue-400 to-purple-500 dark:to-purple-400"></div>
              </h3>
              <ul className="space-y-3">
                {[
                  {
                    label: "Contact Us",
                    to: "/contact",
                    isAnchor: false,
                  },
                  {
                    label: "FAQs",
                    to: user ? "/user/faq" : "/login",
                    isAnchor: isHome,
                  },
                  {
                    label: "Privacy Policy",
                    to: "/privacypolicy",
                    isAnchor: false,
                  },
                  {
                    label: "Terms & Conditions",
                    to: "/terms",
                    isAnchor: false,
                  },
                ].map(({ label, to, isAnchor }, index) => (
                  <li key={index}>
                    {isAnchor ? (
                      <a
                        href={to}
                        className="group flex items-center text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all duration-200"
                      >
                        <FiArrowRight
                          className="mr-2 transform group-hover:translate-x-1 transition-transform duration-200"
                          size={14}
                        />
                        {label}
                      </a>
                    ) : (
                      <Link
                        to={to}
                        className="group flex items-center text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all duration-200"
                      >
                        <FiArrowRight
                          className="mr-2 transform group-hover:translate-x-1 transition-transform duration-200"
                          size={14}
                        />
                        {label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-6 relative">
                Contact Info
                <div className="absolute -bottom-2 left-0 w-8 h-0.5 bg-gradient-to-r from-blue-500 dark:from-blue-400 to-purple-500 dark:to-purple-400"></div>
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="w-8 h-8 bg-blue-200/50 dark:bg-blue-600/20 backdrop-blur-xl rounded-lg flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    <FaEnvelope
                      className="text-blue-600 dark:text-blue-400"
                      size={14}
                    />
                  </div>
                  <div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">
                      Email
                    </p>
                    <a
                      href="mailto:info@educonsultancy.com"
                      className="text-slate-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
                    >
                      info@educonsultancy.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-8 h-8 bg-green-200/50 dark:bg-green-600/20 backdrop-blur-xl rounded-lg flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    <FaPhone
                      className="text-green-600 dark:text-green-400"
                      size={14}
                    />
                  </div>
                  <div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">
                      Phone
                    </p>
                    <a
                      href="tel:+9779876543210"
                      className="text-slate-900 dark:text-white hover:text-green-600 dark:hover:text-green-400 transition-colors duration-200"
                    >
                      +977 9876543210
                    </a>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-8 h-8 bg-purple-200/50 dark:bg-purple-600/20 backdrop-blur-xl rounded-lg flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    <FaMapMarkerAlt
                      className="text-purple-600 dark:text-purple-400"
                      size={14}
                    />
                  </div>
                  <div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">
                      Address
                    </p>
                    <p className="text-slate-900 dark:text-white">
                      Bhaisepati, Lalitpur
                      <br />
                      Nepal
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-300/50 dark:border-slate-700/50 bg-slate-200/30 dark:bg-slate-900/50 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-center text-slate-600 dark:text-slate-400">
              <span>
                © {new Date().getFullYear()} EduConsultancy & Krishna Panthee.
                All rights reserved.
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
